import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import validator from "validator";
import {
  AlertCard,
  Button,
  Modal,
  Pill,
  SelectMenuDropdown,
  TextInputBase,
  ToolTip,
} from "@truevault/aquilo";
import { usePrivacyCenterSettings } from "./PrivacyCenterSettings";
import { usePrimaryOrganization } from "../../../hooks";
import { usePostmarkMailbox } from "../../../hooks/usePostmarkMailbox";
import { useActionRequest } from "../../../../common/hooks/api";
import { UpsertPrivacyCenterDomain } from "../../../../common/service/server/controller/PrivacyCenterController";
import { useEmailSettings } from "./EmailSettings";
import { ACCEPTED_FILE_TYPES, LogoUploadDialog } from "../../dialog/LogoUploadDialog";
import { getPrivacyCenterCnameValue } from "../../../util/privacyCenterUtil";
import { Api } from "../../../../common/service/Api";
import { Loading } from "../..";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import {
  DomainSetupDnsRecord,
  DomainSetupDnsRecordsTable,
  DomainSetupDocumentationLink,
  DomainSetupInlineCopyField,
  DomainSetupStepSection,
} from "./DomainSetupInstructionPrimitives";
import { DomainEmailSetupMode, useProductAnalytics } from "../../../analytics/productAnalytics";
import { getDomainEmailSetupVisibility } from "../../../util/domainEmailSetupVisibility";
import { getTestEmailDisabledReason } from "../../../util/domainSetupTestEmail";
import {
  getPrivacyEmailInitialization,
  POSTMARK_INACTIVE_RECIPIENT_MESSAGE,
} from "../../../util/privacyEmailSetup";

type VerificationState = "unknown" | "pending" | "success" | "error";

const STATUS_LABEL: Record<VerificationState, string> = {
  unknown: "In progress",
  pending: "In progress",
  success: "Successful",
  error: "Check setup",
};

const STATUS_VARIANT: Record<VerificationState, "warning" | "completed" | "error"> = {
  unknown: "warning",
  pending: "warning",
  success: "completed",
  error: "error",
};

const PLACEHOLDER_DOMAIN = "your-domain.com";
const FORWARDING_POLL_INTERVAL_MS = 5000;
const FORWARDING_POLL_TIMEOUT_MS = 60000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const getDomainVerificationErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const responseMessage = error?.response?.data?.message?.trim?.();
  const responseError = error?.response?.data?.error?.trim?.();

  if (status === 409) {
    return "This domain is already in use by another organization. Please use another domain.";
  }

  if (responseMessage) {
    return responseMessage;
  }

  if (responseError && responseError !== "Conflict") {
    return responseError;
  }

  return "We couldn't verify everything. Try again in a few minutes.";
};

function StatusBadge({ state }: { state: VerificationState }) {
  return <Pill variant={STATUS_VARIANT[state]}>{STATUS_LABEL[state]}</Pill>;
}

type DnsRecordRow = DomainSetupDnsRecord & {
  state: VerificationState;
};

type StatusItem = {
  label: string;
  state: VerificationState;
  detail?: string;
  disabled?: boolean;
};

const PRIMARY_DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
const PRIMARY_DOMAIN_ERROR_MESSAGE = "Enter a valid domain like example.com.";
const SETUP_NEW_EMAIL_VALUE = "own";
const PARENT_EMAIL_SETUP_VALUE_PREFIX = "parent:";

const getParentEmailSetupValue = (ownerOrganizationId: string) =>
  `${PARENT_EMAIL_SETUP_VALUE_PREFIX}${ownerOrganizationId}`;

const sanitizeSubdomainLabel = (subdomain: string): string =>
  subdomain
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

const inferPrimaryDomainFromPrivacyDomain = (privacyDomain: string): string => {
  const normalized = privacyDomain.trim().toLowerCase();
  if (!normalized) {
    return "";
  }
  const parts = normalized.split(".").filter(Boolean);
  if (parts.length > 2) {
    return parts.slice(1).join(".");
  }
  return normalized;
};

const inferSubdomainLabelFromPrivacyDomain = (
  privacyDomain: string,
  baseDomain: string,
): string => {
  const normalizedPrivacyDomain = privacyDomain.trim().toLowerCase();
  if (!normalizedPrivacyDomain) {
    return "";
  }

  const normalizedBaseDomain = baseDomain.trim().toLowerCase();
  if (
    normalizedBaseDomain &&
    normalizedPrivacyDomain.endsWith(`.${normalizedBaseDomain}`) &&
    normalizedPrivacyDomain.length > normalizedBaseDomain.length + 1
  ) {
    return sanitizeSubdomainLabel(
      normalizedPrivacyDomain.slice(0, -(normalizedBaseDomain.length + 1)),
    );
  }

  return sanitizeSubdomainLabel(normalizedPrivacyDomain.split(".")[0] ?? "");
};

const isValidPrimaryDomain = (value: string): boolean => {
  const trimmed = value.trim().toLowerCase();
  return Boolean(trimmed) && PRIMARY_DOMAIN_REGEX.test(trimmed);
};

function StatusRow({
  label,
  state,
  detail,
  disabled,
}: {
  label: string;
  state: VerificationState;
  detail?: string;
  disabled?: boolean;
}) {
  return (
    <div
      className={clsx(
        "flex items-center justify-between gap-[16px] text-[14px] text-neutral-900",
        disabled && "opacity-50",
      )}
    >
      <div className="flex flex-col gap-[2px]">
        <span>{label}</span>
        {detail && <span className="font-mono text-[12px] text-neutral-500">{detail}</span>}
      </div>
      <div className="flex min-w-[120px] justify-end">
        <StatusBadge state={state} />
      </div>
    </div>
  );
}

function RecordStatusIcon({ state }: { state: VerificationState }) {
  const wrapperClass = "inline-flex h-[20px] w-[20px] shrink-0 items-center justify-center";

  if (state === "success") {
    return (
      <span className={wrapperClass} aria-hidden={true}>
        <CheckCircleIcon
          className="h-full w-full text-green-500"
          style={{ color: "var(--tw-green-500, #6EE7B7)" }}
        />
      </span>
    );
  }

  if (state === "error") {
    return (
      <span className={wrapperClass} aria-hidden={true}>
        <XCircleIcon className="h-full w-full text-red-500" />
      </span>
    );
  }

  return null;
}

export type DomainSetupProgress = {
  ready: boolean;
  hasSavedDomain: boolean;
  canProceed: boolean;
  verificationDisabled: boolean;
  logoUploaded: boolean;
  testEmailComplete: boolean;
  setupMode?: DomainEmailSetupMode;
};

type DomainSetupCardProps = {
  className?: string;
  hideCardChrome?: boolean;
  onStateChange?(state: DomainSetupProgress): void;
  trackOnboardingAnalytics?: boolean;
};

export const DomainSetupCard = ({
  className,
  hideCardChrome = false,
  onStateChange,
  trackOnboardingAnalytics = false,
}: DomainSetupCardProps) => {
  const captureProductAnalyticsEvent = useProductAnalytics({
    flow: "privacy_email_domain_setup",
  });
  const analyticsFlowStartedRef = useRef(false);
  const { privacyCenter } = usePrivacyCenterSettings();
  const [org, , refreshOrg] = usePrimaryOrganization();
  const organizationId = org?.id;

  const {
    mailbox: postmarkMailbox,
    request: postmarkMailboxRequest,
    upsert: upsertPostmarkMailbox,
    upsertRequest: postmarkUpsertRequest,
    verify: verifyPostmarkMailbox,
    verifyRequest: postmarkVerifyRequest,
    sendTest: sendPostmarkTest,
    refresh: refreshPostmarkMailbox,
    share: postmarkMailboxShare,
    shareOptions: postmarkMailboxShareOptions,
    upsertShare: upsertPostmarkMailboxShare,
    upsertShareRequest: postmarkShareUpsertRequest,
    deleteShare: deletePostmarkMailboxShare,
    deleteShareRequest: postmarkShareDeleteRequest,
  } = usePostmarkMailbox(organizationId);

  const { fetch: updatePrivacyCenterDomain, request: privacyDomainUpdateRequest } =
    useActionRequest<unknown, UpsertPrivacyCenterDomain>({
      api: (payload) =>
        Api.privacyCenter.updatePrivacyCenterDomain(organizationId, privacyCenter?.id, payload),
      notReady: !organizationId || !privacyCenter?.id,
      onError: (request, error: any) => {
        const responseMessage = error?.response?.data?.message?.trim?.();
        const responseError = error?.response?.data?.error?.trim?.();
        request.errorMessage =
          responseMessage ||
          (error?.response?.status === 409
            ? "This privacy domain is already in use by another Privacy Center."
            : responseError) ||
          "Unable to save privacy center domain.";
      },
    });

  const postmarkMailboxRef = useRef(postmarkMailbox);
  React.useEffect(() => {
    postmarkMailboxRef.current = postmarkMailbox;
  }, [postmarkMailbox]);

  const { settings: emailSettings, organization: messageOrg } = useEmailSettings();
  const effectiveOrg = messageOrg ?? org;
  const sharedPostmarkMailbox = effectiveOrg?.sharedPostmarkMailbox ?? postmarkMailboxShare;
  const isSharedPostmark =
    effectiveOrg?.postmarkMailboxMode === "SHARED" || Boolean(sharedPostmarkMailbox);
  const shareOptions = useMemo(
    () => postmarkMailboxShareOptions ?? [],
    [postmarkMailboxShareOptions],
  );
  const sendMethod = effectiveOrg?.sendMethod;
  const { hasParentEmailSetupOptions, isNylas, usesOwnedPostmarkMailbox, canShowSetupDetails } =
    getDomainEmailSetupVisibility({
      sendMethod,
      hasOwnedPostmarkMailbox: Boolean(postmarkMailbox),
      isSharedPostmark,
      hasShareOptions: shareOptions.length > 0,
    });
  const setupMode: DomainEmailSetupMode = isNylas
    ? "nylas"
    : isSharedPostmark
    ? "shared_postmark"
    : "owned_postmark";

  const trackStepViewed = useCallback(
    (stepId: string) => {
      if (trackOnboardingAnalytics) {
        captureProductAnalyticsEvent("onboarding_step_viewed", {
          step_id: stepId,
          section: "domain_email_setup",
          setup_mode: setupMode,
        });
      }
    },
    [captureProductAnalyticsEvent, setupMode, trackOnboardingAnalytics],
  );

  const trackStepCompleted = useCallback(
    (stepId: string, completionSource: string) => {
      if (trackOnboardingAnalytics) {
        captureProductAnalyticsEvent("onboarding_step_completed", {
          step_id: stepId,
          section: "domain_email_setup",
          setup_mode: setupMode,
          completion_source: completionSource,
        });
      }
    },
    [captureProductAnalyticsEvent, setupMode, trackOnboardingAnalytics],
  );

  const trackStepFailed = useCallback(
    (stepId: string, failedChecks: string[]) => {
      if (trackOnboardingAnalytics) {
        captureProductAnalyticsEvent("onboarding_step_failed", {
          step_id: stepId,
          section: "domain_email_setup",
          setup_mode: setupMode,
          failed_checks: failedChecks,
        });
      }
    },
    [captureProductAnalyticsEvent, setupMode, trackOnboardingAnalytics],
  );
  const sharedChildOrganizations = postmarkMailbox?.sharedWithChildOrganizations ?? [];
  const postmarkEmailLocked = sharedChildOrganizations.length > 0;
  const sharedChildOrganizationNames = sharedChildOrganizations
    .map((childOrg) => childOrg.organizationName)
    .join(", ");
  const postmarkEmailLockTooltip = postmarkEmailLocked
    ? `This Postmark email is locked because it is used by child organizations: ${sharedChildOrganizationNames}. Remove sharing before changing it.`
    : null;

  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [privacyRootDomainInput, setPrivacyRootDomainInput] = useState("");
  const [privacyRootDomainSaved, setPrivacyRootDomainSaved] = useState("");
  const [privacyEmailInput, setPrivacyEmailInput] = useState("");
  const [privacyEmailSaved, setPrivacyEmailSaved] = useState("");
  const [privacyDomainStatus, setPrivacyDomainStatus] = useState<VerificationState>("unknown");
  const [verificationRunning, setVerificationRunning] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [dnsVerificationAttempted, setDnsVerificationAttempted] = useState(false);
  const testEmailSent = Boolean(postmarkMailbox?.lastTestEmailSentAt);
  const [showDomainChangeConfirmModal, setShowDomainChangeConfirmModal] = useState(false);
  const [showShareConfirmModal, setShowShareConfirmModal] = useState(false);
  const [pendingShareOwnerId, setPendingShareOwnerId] = useState<string | null>(null);
  const [pendingDomainChange, setPendingDomainChange] = useState<string | null>(null);
  const [privacySubdomainLabel, setPrivacySubdomainLabel] = useState("privacy");
  const [privacySubdomainSaved, setPrivacySubdomainSaved] = useState("privacy");

  const [privacyCustomDomain, setPrivacyCustomDomain] = useState<string>("");
  const privacyDomainInitializedRef = useRef(false);
  const privacyEmailInitializedRef = useRef(false);
  const sendMethodBootstrapStartedRef = useRef(false);
  const autoVerifiedPrivacyDomainRef = useRef<string | null>(null);
  const autoVerifyPrivacyDomainRunningRef = useRef(false);

  useEffect(() => {
    const customDomain = privacyCenter?.domains?.[0]?.customDomain?.trim().toLowerCase();
    if (!customDomain) {
      return;
    }

    setPrivacyCustomDomain(customDomain);
    if (privacyDomainInitializedRef.current) {
      return;
    }

    const rootDomain = inferPrimaryDomainFromPrivacyDomain(customDomain);
    const subdomain = inferSubdomainLabelFromPrivacyDomain(customDomain, rootDomain) || "privacy";
    setPrivacyRootDomainInput(rootDomain);
    setPrivacyRootDomainSaved(rootDomain);
    setPrivacySubdomainLabel(subdomain);
    setPrivacySubdomainSaved(subdomain);
    privacyDomainInitializedRef.current = true;
  }, [privacyCenter?.domains]);

  useEffect(() => {
    const initialization = getPrivacyEmailInitialization({
      persistedMailbox: postmarkMailbox?.mailbox,
      savedMailbox: privacyEmailSaved,
      initialized: privacyEmailInitializedRef.current,
      mailboxRequestSucceeded: Boolean(postmarkMailboxRequest?.success),
      rootDomain: privacyRootDomainSaved,
    });
    if (!initialization) {
      return;
    }

    setPrivacyEmailInput(initialization.email);
    if (initialization.persisted) {
      setPrivacyEmailSaved(initialization.email);
    }
    privacyEmailInitializedRef.current = true;
  }, [
    postmarkMailbox?.mailbox,
    postmarkMailboxRequest?.success,
    privacyEmailSaved,
    privacyRootDomainSaved,
  ]);

  const effectiveRootDomain = useMemo(() => {
    return privacyRootDomainInput.trim() || privacyRootDomainSaved || PLACEHOLDER_DOMAIN;
  }, [privacyRootDomainInput, privacyRootDomainSaved]);

  const privacySubdomainValue = useMemo(() => {
    return sanitizeSubdomainLabel(privacySubdomainLabel) || "privacy";
  }, [privacySubdomainLabel]);

  const privacySubdomainSavedValue = useMemo(() => {
    return sanitizeSubdomainLabel(privacySubdomainSaved) || "privacy";
  }, [privacySubdomainSaved]);

  const effectivePrivacySubdomainValue = useMemo(() => {
    return privacyRootDomainSaved ? privacySubdomainSavedValue : privacySubdomainValue;
  }, [privacyRootDomainSaved, privacySubdomainSavedValue, privacySubdomainValue]);

  const privacySubdomainSuffix = useMemo(() => {
    return (privacyRootDomainInput.trim() || privacyRootDomainSaved).toLowerCase();
  }, [privacyRootDomainInput, privacyRootDomainSaved]);

  const resolvedPrivacyDomain = useMemo(() => {
    const baseDomain = (privacyRootDomainSaved || privacyRootDomainInput.trim()).toLowerCase();
    const existingDomain = privacyCustomDomain || privacyCenter?.domains?.[0]?.customDomain || "";
    if (!baseDomain) {
      return existingDomain;
    }
    return `${effectivePrivacySubdomainValue}.${baseDomain}`;
  }, [
    effectivePrivacySubdomainValue,
    privacyRootDomainInput,
    privacyRootDomainSaved,
    privacyCenter?.domains,
    privacyCustomDomain,
  ]);

  const emailForwardAddress =
    postmarkMailbox?.mailbox ||
    privacyEmailSaved ||
    privacyEmailInput ||
    `privacy@${effectiveRootDomain}`;
  const setupCompleteDomainLabel =
    postmarkMailbox?.sendingDomain || privacyRootDomainSaved || privacyRootDomainInput.trim();
  const organizationSpecificPrivacyEmail =
    postmarkMailbox?.mailbox || privacyEmailSaved || privacyEmailInput;

  const inboundAddress = postmarkMailbox?.inboundAddress ?? "";
  const testEmailRecipient =
    emailSettings?.testEmailAddress ?? "the email address you used to log in";

  const hasSavedDomain = useMemo(() => Boolean(privacyRootDomainSaved), [privacyRootDomainSaved]);
  const primaryDomainValidationError = useMemo(() => {
    const trimmed = privacyRootDomainInput.trim();
    if (!trimmed) {
      return null;
    }

    if (!isValidPrimaryDomain(trimmed)) {
      return PRIMARY_DOMAIN_ERROR_MESSAGE;
    }

    return null;
  }, [privacyRootDomainInput]);
  const privacyEmailValidationError = useMemo(() => {
    const trimmed = privacyEmailInput.trim();
    if (!trimmed) {
      return null;
    }
    return validator.isEmail(trimmed)
      ? null
      : "Enter a valid privacy email like privacy@example.com.";
  }, [privacyEmailInput]);
  const shouldShowEmailSetupStep = hasSavedDomain && hasParentEmailSetupOptions;
  const emailSetupSelectionComplete =
    !shouldShowEmailSetupStep || usesOwnedPostmarkMailbox || isSharedPostmark;

  const handleSendTestEmail = useCallback(async () => {
    try {
      await emailSettings.sendTestMessage();
      trackStepCompleted("test_email", "send_test_email");
    } catch {
      // Errors are surfaced via snackbar notifications; no-op here.
    }
  }, [emailSettings, trackStepCompleted]);

  const resetPrivacyDomainVerificationState = useCallback((nextDomain: string) => {
    setVerificationError(null);
    setPrivacyDomainStatus(nextDomain ? "pending" : "unknown");
  }, []);

  const handlePrimaryDomainSave = useCallback(
    async (nextDomain?: string) => {
      const trimmed = (nextDomain ?? privacyRootDomainInput).trim().toLowerCase();
      if (!trimmed) {
        return;
      }
      if (!isValidPrimaryDomain(trimmed)) {
        return;
      }
      if (!organizationId) {
        return;
      }

      const domainChanged = Boolean(privacyRootDomainSaved && trimmed !== privacyRootDomainSaved);
      const nextPrivacyDomain = `${privacySubdomainValue}.${trimmed}`;
      const currentPrivacyDomain = (
        privacyCustomDomain ||
        privacyCenter?.domains?.[0]?.customDomain ||
        ""
      )
        .trim()
        .toLowerCase();
      const privacyDomainChanged = nextPrivacyDomain !== currentPrivacyDomain;

      try {
        if (privacyCenter?.id && privacyDomainChanged) {
          await updatePrivacyCenterDomain({
            privacyCenterId: privacyCenter.id,
            customDomain: nextPrivacyDomain,
          });
          setPrivacyCustomDomain(nextPrivacyDomain);
        }
        setPrivacySubdomainSaved(privacySubdomainValue);
        setPrivacyRootDomainInput(trimmed);
        setPrivacyRootDomainSaved(trimmed);
        trackStepCompleted("domain_address", domainChanged ? "update_domain" : "save_domain");
        if (domainChanged) {
          resetPrivacyDomainVerificationState(trimmed);
        } else if (privacyDomainChanged) {
          setVerificationError(null);
          setPrivacyDomainStatus("pending");
        }
      } catch {
        // errors are surfaced via snackbar notifications; no-op here
      }
    },
    [
      organizationId,
      privacyRootDomainSaved,
      privacyRootDomainInput,
      privacyCenter?.domains,
      privacyCenter?.id,
      privacyCustomDomain,
      privacySubdomainValue,
      resetPrivacyDomainVerificationState,
      updatePrivacyCenterDomain,
      trackStepCompleted,
    ],
  );

  const handlePrimaryDomainSaveRequest = useCallback(() => {
    const trimmed = privacyRootDomainInput.trim().toLowerCase();
    if (!trimmed) {
      return;
    }
    if (!isValidPrimaryDomain(trimmed)) {
      return;
    }

    const changingDomain = Boolean(privacyRootDomainSaved && trimmed !== privacyRootDomainSaved);
    const changingSubdomain = Boolean(
      privacyRootDomainSaved && privacySubdomainValue !== privacySubdomainSavedValue,
    );
    const hasVerifiedPrivacyDomain = privacyDomainStatus === "success";

    if ((changingDomain || changingSubdomain) && hasVerifiedPrivacyDomain) {
      setPendingDomainChange(trimmed);
      setShowDomainChangeConfirmModal(true);
      return;
    }

    void handlePrimaryDomainSave(trimmed);
  }, [
    handlePrimaryDomainSave,
    privacyRootDomainInput,
    privacyRootDomainSaved,
    privacyDomainStatus,
    privacySubdomainSavedValue,
    privacySubdomainValue,
  ]);

  const handleConfirmPrimaryDomainChange = useCallback(() => {
    const nextDomain = pendingDomainChange;
    setShowDomainChangeConfirmModal(false);
    setPendingDomainChange(null);

    if (!nextDomain) {
      return;
    }
    void handlePrimaryDomainSave(nextDomain);
  }, [handlePrimaryDomainSave, pendingDomainChange]);

  const pendingShareOption = useMemo(
    () => shareOptions.find((option) => option.ownerOrganizationId === pendingShareOwnerId),
    [pendingShareOwnerId, shareOptions],
  );
  const emailSetupParentOptions = useMemo(() => {
    if (
      sharedPostmarkMailbox &&
      !shareOptions.some(
        (option) => option.ownerOrganizationId === sharedPostmarkMailbox.ownerOrganizationId,
      )
    ) {
      return [sharedPostmarkMailbox, ...shareOptions];
    }

    return shareOptions;
  }, [shareOptions, sharedPostmarkMailbox]);
  const emailSetupSelectValue =
    isSharedPostmark && sharedPostmarkMailbox
      ? getParentEmailSetupValue(sharedPostmarkMailbox.ownerOrganizationId)
      : "";
  const emailSetupSelectDisabled =
    postmarkUpsertRequest?.running ||
    postmarkShareUpsertRequest?.running ||
    postmarkShareDeleteRequest?.running;
  const emailSetupDropdownItems = useMemo(
    () => [
      {
        label: organizationSpecificPrivacyEmail || "privacy@your-domain.com",
        previewLabel: organizationSpecificPrivacyEmail || "privacy@your-domain.com",
        value: SETUP_NEW_EMAIL_VALUE,
        isSelected: usesOwnedPostmarkMailbox,
      },
      ...emailSetupParentOptions.map((option) => ({
        label: option.emailAddress,
        previewLabel: option.emailAddress,
        value: getParentEmailSetupValue(option.ownerOrganizationId),
        isSelected: emailSetupSelectValue === getParentEmailSetupValue(option.ownerOrganizationId),
      })),
    ],
    [
      emailSetupParentOptions,
      emailSetupSelectValue,
      organizationSpecificPrivacyEmail,
      usesOwnedPostmarkMailbox,
    ],
  );

  const handleShareOptionClick = useCallback((ownerOrganizationId: string) => {
    setPendingShareOwnerId(ownerOrganizationId);
    setShowShareConfirmModal(true);
  }, []);

  const handleConfirmShare = useCallback(async () => {
    if (!pendingShareOwnerId) {
      return;
    }

    try {
      await upsertPostmarkMailboxShare({ ownerOrganizationId: pendingShareOwnerId });
      await refreshOrg();
      setShowShareConfirmModal(false);
      setPendingShareOwnerId(null);
      trackStepCompleted("privacy_email_selection", "select_parent_email");
    } catch {
      // errors are surfaced via snackbar notifications; no-op here
    }
  }, [pendingShareOwnerId, refreshOrg, trackStepCompleted, upsertPostmarkMailboxShare]);

  const handlePrivacyEmailSave = useCallback(async (): Promise<boolean> => {
    const mailbox = privacyEmailInput.trim().toLowerCase();
    if (
      postmarkUpsertRequest?.running ||
      postmarkEmailLocked ||
      !mailbox ||
      !validator.isEmail(mailbox)
    ) {
      return false;
    }

    try {
      await upsertPostmarkMailbox({ mailbox });
      setPrivacyEmailInput(mailbox);
      setPrivacyEmailSaved(mailbox);
      setVerificationError(null);
      setDnsVerificationAttempted(false);
      return true;
    } catch {
      // errors are surfaced via snackbar notifications; no-op here
      return false;
    }
  }, [
    postmarkEmailLocked,
    postmarkUpsertRequest?.running,
    privacyEmailInput,
    upsertPostmarkMailbox,
  ]);

  const handleOwnPostmarkMailboxSetup = useCallback(async (): Promise<boolean> => {
    const saved = await handlePrivacyEmailSave();
    if (saved) {
      await refreshOrg();
    }
    return saved;
  }, [handlePrivacyEmailSave, refreshOrg]);

  const handleSetupNewEmailSelection = useCallback(async () => {
    if (postmarkShareDeleteRequest?.running) {
      return;
    }

    try {
      if (isSharedPostmark) {
        await deletePostmarkMailboxShare();
        await refreshOrg();
      }

      const setupSucceeded = await handleOwnPostmarkMailboxSetup();
      if (setupSucceeded) {
        trackStepCompleted("privacy_email_selection", "select_organization_email");
      }
    } catch {
      // errors are surfaced via snackbar notifications; no-op here
    }
  }, [
    deletePostmarkMailboxShare,
    handleOwnPostmarkMailboxSetup,
    isSharedPostmark,
    postmarkShareDeleteRequest?.running,
    refreshOrg,
    trackStepCompleted,
  ]);

  const handleEmailSetupSelectionChange = useCallback(
    (value: string) => {
      if (value === SETUP_NEW_EMAIL_VALUE) {
        void handleSetupNewEmailSelection();
        return;
      }

      if (value.startsWith(PARENT_EMAIL_SETUP_VALUE_PREFIX)) {
        const ownerOrganizationId = value.slice(PARENT_EMAIL_SETUP_VALUE_PREFIX.length);
        if (
          ownerOrganizationId &&
          ownerOrganizationId !== sharedPostmarkMailbox?.ownerOrganizationId
        ) {
          handleShareOptionClick(ownerOrganizationId);
        }
      }
    },
    [
      handleSetupNewEmailSelection,
      handleShareOptionClick,
      sharedPostmarkMailbox?.ownerOrganizationId,
    ],
  );

  const handleVerification = useCallback(async () => {
    if (!privacyCenter || !organizationId) {
      return;
    }

    const domainToVerify = resolvedPrivacyDomain;
    if (!domainToVerify) {
      setVerificationError("Add a privacy subdomain before running these checks.");
      setPrivacyDomainStatus("error");
      trackStepFailed("setup_verification", ["privacy_cname"]);
      return;
    }

    setVerificationRunning(true);
    setVerificationError(null);
    setPrivacyDomainStatus("pending");

    try {
      const domainVerified = await Api.privacyCenter.verifyPrivacyCenterDomain(
        privacyCenter.organizationId,
        privacyCenter.id,
        domainToVerify,
      );
      setPrivacyDomainStatus(domainVerified ? "success" : "error");

      if (usesOwnedPostmarkMailbox) {
        const verifiedMailbox = await verifyPostmarkMailbox();
        if (verifiedMailbox) {
          postmarkMailboxRef.current = verifiedMailbox;
        }

        const dkimVerified = Boolean(postmarkMailboxRef.current?.dkimVerifiedAt);
        const returnPathVerified = Boolean(postmarkMailboxRef.current?.returnPathVerifiedAt);
        const coreDnsVerified = dkimVerified && returnPathVerified;
        if (!coreDnsVerified) {
          setDnsVerificationAttempted(true);
          const failedRecords: string[] = [];
          const failedChecks: string[] = [];
          if (!dkimVerified) {
            failedRecords.push("DKIM");
            failedChecks.push("dkim");
          }
          if (!returnPathVerified) {
            failedRecords.push("return-path");
            failedChecks.push("return_path");
          }
          if (!domainVerified) {
            failedRecords.push("privacy CNAME");
            failedChecks.push("privacy_cname");
          }

          const failedSummary = failedRecords.length
            ? failedRecords.join(", ")
            : "DKIM and return-path";
          setVerificationError(
            `We couldn't verify ${failedSummary} yet. Check those DNS records and rerun setup.`,
          );
          trackStepFailed("dns_records", failedChecks);
          trackStepFailed("setup_verification", failedChecks);
          // Don't run the forwarding test until DKIM/return-path are verified, since many
          // providers will refuse forwarding without them and it creates confusing failures.
          return;
        }
        setDnsVerificationAttempted(false);

        if (domainVerified) {
          trackStepCompleted("dns_records", "verification_check");
        } else {
          trackStepFailed("dns_records", ["privacy_cname"]);
        }

        const mailboxAddress = postmarkMailboxRef.current?.mailbox?.trim().toLowerCase();
        if (mailboxAddress) {
          try {
            await sendPostmarkTest({
              fromEmail: mailboxAddress,
              toEmail: mailboxAddress,
              subject: "Privacy email forwarding test",
              textBody: "This is a forwarding test from Polaris.",
            });
          } catch (error: any) {
            if (error?.response?.status === 422) {
              setVerificationError(POSTMARK_INACTIVE_RECIPIENT_MESSAGE);
              trackStepFailed("email_forwarding", ["forwarding"]);
              trackStepFailed("setup_verification", ["forwarding"]);
              return;
            }
            throw error;
          }
        } else {
          setVerificationError("Save your privacy email before running the forwarding test.");
          trackStepFailed("setup_verification", ["forwarding"]);
          return;
        }

        let forwardingConfirmed = postmarkMailboxRef.current?.forwardingStatus === "CONFIRMED";
        let forwardingFailed = postmarkMailboxRef.current?.forwardingStatus === "FAILED";

        if (!forwardingConfirmed && !forwardingFailed) {
          const start = Date.now();

          while (Date.now() - start < FORWARDING_POLL_TIMEOUT_MS) {
            await sleep(FORWARDING_POLL_INTERVAL_MS);
            const refreshedMailbox = await refreshPostmarkMailbox();
            if (typeof refreshedMailbox !== "undefined") {
              postmarkMailboxRef.current = refreshedMailbox ?? null;
            }

            const latestMailbox = postmarkMailboxRef.current;
            if (latestMailbox?.forwardingStatus === "CONFIRMED") {
              forwardingConfirmed = true;
              break;
            }

            if (latestMailbox?.forwardingStatus === "FAILED") {
              forwardingFailed = true;
              break;
            }
          }
        }

        if (!forwardingConfirmed) {
          trackStepFailed("email_forwarding", ["forwarding"]);
          if (!forwardingFailed) {
            setVerificationError(
              "We sent a test email but haven't received it yet. Double-check your forwarding rule and rerun the test when ready.",
            );
          }
        } else {
          trackStepCompleted("email_forwarding", "verification_check");
        }

        const failedChecks: string[] = [];
        if (!domainVerified) {
          failedChecks.push("privacy_cname");
        }
        if (!forwardingConfirmed) {
          failedChecks.push("forwarding");
        }

        if (failedChecks.length === 0) {
          trackStepCompleted("setup_verification", "verification_check");
        } else {
          trackStepFailed("setup_verification", failedChecks);
        }
      } else {
        const failedChecks: string[] = [];
        if (!domainVerified) {
          failedChecks.push("privacy_cname");
          trackStepFailed("dns_records", ["privacy_cname"]);
        } else {
          trackStepCompleted("dns_records", "verification_check");
        }

        if (isSharedPostmark && sharedPostmarkMailbox?.status !== "VERIFIED") {
          failedChecks.push("shared_email_setup");
        }

        if (failedChecks.length === 0) {
          trackStepCompleted("setup_verification", "verification_check");
        } else {
          trackStepFailed("setup_verification", failedChecks);
        }
      }
    } catch (err: any) {
      setPrivacyDomainStatus("error");
      setVerificationError(getDomainVerificationErrorMessage(err));
      trackStepFailed("setup_verification", ["verification_request"]);
    } finally {
      setVerificationRunning(false);
    }
  }, [
    organizationId,
    privacyCenter,
    refreshPostmarkMailbox,
    resolvedPrivacyDomain,
    sendPostmarkTest,
    isSharedPostmark,
    sharedPostmarkMailbox?.status,
    trackStepCompleted,
    trackStepFailed,
    usesOwnedPostmarkMailbox,
    verifyPostmarkMailbox,
  ]);

  useEffect(() => {
    const targetDomain = resolvedPrivacyDomain?.toLowerCase();
    if (!targetDomain) {
      setPrivacyDomainStatus("unknown");
      return;
    }

    const domainRecord = privacyCenter?.domains?.find(
      (domain) => domain?.customDomain?.toLowerCase() === targetDomain,
    );

    if (!domainRecord) {
      setPrivacyDomainStatus(hasSavedDomain ? "pending" : "unknown");
      return;
    }

    const certificateStatus = domainRecord.certificateStatus?.toUpperCase();
    if (certificateStatus === "FAILURE" || certificateStatus === "INVALID_CERTIFICATE") {
      setPrivacyDomainStatus("error");
      return;
    }

    if (certificateStatus === "VALID_CERTIFICATE") {
      setPrivacyDomainStatus("success");
      return;
    }

    // Keep explicit verify-success within this page session, but don't infer success
    // from mere domain row existence.
    if (privacyDomainStatus === "success") {
      return;
    }

    setPrivacyDomainStatus("pending");
  }, [privacyCenter?.domains, resolvedPrivacyDomain, hasSavedDomain, privacyDomainStatus]);

  useEffect(() => {
    if (!privacyCenter || !organizationId || !hasSavedDomain) {
      return;
    }

    const domainToVerify = resolvedPrivacyDomain?.trim().toLowerCase();
    if (!domainToVerify) {
      return;
    }

    if (
      autoVerifyPrivacyDomainRunningRef.current ||
      autoVerifiedPrivacyDomainRef.current === domainToVerify
    ) {
      return;
    }

    autoVerifyPrivacyDomainRunningRef.current = true;
    void (async () => {
      try {
        const domainVerified = await Api.privacyCenter.verifyPrivacyCenterDomain(
          privacyCenter.organizationId,
          privacyCenter.id,
          domainToVerify,
        );
        setPrivacyDomainStatus(domainVerified ? "success" : "error");
      } catch {
        // Keep current inferred state if passive verify fails; explicit verify action still surfaces errors.
      } finally {
        autoVerifiedPrivacyDomainRef.current = domainToVerify;
        autoVerifyPrivacyDomainRunningRef.current = false;
      }
    })();
  }, [organizationId, hasSavedDomain, privacyCenter, resolvedPrivacyDomain]);

  useEffect(() => {
    if (postmarkMailbox?.forwardingStatus === "CONFIRMED") {
      setVerificationError(null);
    }
  }, [postmarkMailbox?.forwardingStatus]);

  const forwardingStatus: VerificationState = useMemo(() => {
    const status = postmarkMailbox?.forwardingStatus;
    if (status === "CONFIRMED") {
      return "success";
    }
    if (status === "FAILED") {
      return "error";
    }
    return "pending";
  }, [postmarkMailbox?.forwardingStatus]);

  const dnsStatus = useCallback(
    (timestamp?: string | null): VerificationState => {
      if (timestamp) {
        return "success";
      }
      return dnsVerificationAttempted ? "error" : "pending";
    },
    [dnsVerificationAttempted],
  );

  const postmarkCoreDnsVerified = Boolean(
    postmarkMailbox?.dkimVerifiedAt && postmarkMailbox?.returnPathVerifiedAt,
  );
  const postmarkDkimHost = postmarkMailbox?.dkimPendingHost ?? postmarkMailbox?.dkimHost;
  const postmarkDkimValue = postmarkMailbox?.dkimPendingTextValue ?? postmarkMailbox?.dkimTextValue;
  const postmarkReturnPathDomain = postmarkMailbox?.returnPathDomain;
  const postmarkReturnPathValue = postmarkMailbox?.returnPathCnameValue;

  const hasDnsRecords = Boolean(
    postmarkDkimHost && postmarkDkimValue && postmarkReturnPathDomain && postmarkReturnPathValue,
  );

  const dnsRecords: DnsRecordRow[] = useMemo(() => {
    const privacyRecord: DnsRecordRow = {
      id: "privacy",
      recordType: "CNAME (Privacy Domain)",
      name: effectivePrivacySubdomainValue,
      value: getPrivacyCenterCnameValue(privacyCenter),
      state: privacyDomainStatus,
    };

    if (isNylas || isSharedPostmark) {
      return [privacyRecord];
    }

    if (!hasDnsRecords || !postmarkMailbox) {
      return [];
    }

    return [
      {
        id: "dkim",
        recordType: "TXT (DKIM)",
        name: postmarkDkimHost,
        value: postmarkDkimValue,
        state: dnsStatus(postmarkMailbox?.dkimVerifiedAt),
      },
      {
        id: "return-path",
        recordType: "CNAME (Return-path)",
        name: postmarkReturnPathDomain,
        value: postmarkReturnPathValue,
        state: dnsStatus(postmarkMailbox?.returnPathVerifiedAt),
      },
      privacyRecord,
    ];
  }, [
    dnsStatus,
    hasDnsRecords,
    isNylas,
    isSharedPostmark,
    postmarkDkimHost,
    postmarkDkimValue,
    postmarkMailbox,
    postmarkReturnPathDomain,
    postmarkReturnPathValue,
    privacyCenter,
    privacyDomainStatus,
    effectivePrivacySubdomainValue,
  ]);

  const statusItems: StatusItem[] = useMemo(() => {
    if (isNylas || isSharedPostmark) {
      return [
        {
          label: resolvedPrivacyDomain
            ? `CNAME (${resolvedPrivacyDomain}) record`
            : "CNAME (privacy domain) record",
          state: privacyDomainStatus,
        },
      ];
    }

    return [
      {
        label: "TXT (DKIM) record",
        state: dnsStatus(postmarkMailbox?.dkimVerifiedAt),
      },
      {
        label: "CNAME (return-path) record",
        state: dnsStatus(postmarkMailbox?.returnPathVerifiedAt),
      },
      {
        label: "Email forwarding",
        state: postmarkCoreDnsVerified ? forwardingStatus : ("unknown" as VerificationState),
        disabled: !postmarkCoreDnsVerified,
        detail: postmarkCoreDnsVerified
          ? undefined
          : "Complete DKIM and return-path DNS records to enable this check.",
      },
      {
        label: resolvedPrivacyDomain
          ? `CNAME (${resolvedPrivacyDomain}) record`
          : "CNAME (privacy domain) record",
        state: privacyDomainStatus,
      },
    ];
  }, [
    dnsStatus,
    forwardingStatus,
    isNylas,
    isSharedPostmark,
    postmarkCoreDnsVerified,
    postmarkMailbox?.dkimVerifiedAt,
    postmarkMailbox?.returnPathVerifiedAt,
    privacyDomainStatus,
    resolvedPrivacyDomain,
  ]);

  const verificationDisabled =
    verificationRunning ||
    postmarkUpsertRequest?.running ||
    (usesOwnedPostmarkMailbox &&
      (!postmarkMailbox || postmarkMailboxRequest?.running || postmarkVerifyRequest?.running));

  const sharedSetupComplete = Boolean(
    isSharedPostmark && sharedPostmarkMailbox?.status === "VERIFIED",
  );
  const canProceed = isNylas
    ? privacyDomainStatus === "success"
    : isSharedPostmark
    ? sharedSetupComplete && privacyDomainStatus === "success"
    : postmarkCoreDnsVerified &&
      forwardingStatus === "success" &&
      privacyDomainStatus === "success";
  const logoUploaded =
    isNylas || isSharedPostmark || Boolean(messageOrg?.messageSettings?.headerImageUrl);
  const allPostmarkSetupComplete =
    usesOwnedPostmarkMailbox &&
    postmarkCoreDnsVerified &&
    forwardingStatus === "success" &&
    privacyDomainStatus === "success";
  const testEmailDisabledReason = getTestEmailDisabledReason({
    logoUploaded,
    dkimVerified: Boolean(postmarkMailbox?.dkimVerifiedAt),
    returnPathVerified: Boolean(postmarkMailbox?.returnPathVerifiedAt),
    forwardingConfirmed: forwardingStatus === "success",
    sending: Boolean(emailSettings?.testMessageRunning),
  });
  const testEmailComplete = !usesOwnedPostmarkMailbox || testEmailSent;
  const forwardingStepNumber = shouldShowEmailSetupStep ? "3" : "2";
  const dnsStepNumber = isSharedPostmark
    ? "3"
    : usesOwnedPostmarkMailbox
    ? shouldShowEmailSetupStep
      ? "4"
      : "3"
    : "2";
  const verificationStepNumber = isSharedPostmark
    ? "4"
    : usesOwnedPostmarkMailbox
    ? shouldShowEmailSetupStep
      ? "5"
      : "4"
    : "3";
  const logoStepNumber = shouldShowEmailSetupStep ? "6" : "5";
  const testEmailStepNumber = shouldShowEmailSetupStep ? "7" : "6";

  const ready = Boolean(privacyCenter && emailSettings && messageOrg);

  useEffect(() => {
    if (!trackOnboardingAnalytics || !ready || analyticsFlowStartedRef.current) {
      return;
    }

    analyticsFlowStartedRef.current = true;
    captureProductAnalyticsEvent("onboarding_flow_started", {
      entry_step_id: "domain_address",
      is_resumed: hasSavedDomain,
      setup_mode: setupMode,
    });
  }, [captureProductAnalyticsEvent, hasSavedDomain, ready, setupMode, trackOnboardingAnalytics]);

  useEffect(() => {
    if (
      !ready ||
      !organizationId ||
      sendMethod !== "POSTMARK" ||
      sendMethodBootstrapStartedRef.current
    ) {
      return;
    }

    sendMethodBootstrapStartedRef.current = true;
    void (async () => {
      try {
        await refreshPostmarkMailbox();
      } catch {
        sendMethodBootstrapStartedRef.current = false;
      }
    })();
  }, [organizationId, ready, refreshPostmarkMailbox, sendMethod]);

  useEffect(() => {
    if (!onStateChange) {
      return;
    }

    onStateChange({
      ready,
      hasSavedDomain,
      canProceed,
      verificationDisabled,
      logoUploaded,
      testEmailComplete,
      setupMode,
    });
  }, [
    ready,
    hasSavedDomain,
    canProceed,
    verificationDisabled,
    logoUploaded,
    testEmailComplete,
    onStateChange,
    setupMode,
  ]);

  if (!ready) {
    return <Loading />;
  }

  const emailSetupDropdown = (showPlaceholder: boolean) => (
    <SelectMenuDropdown<string>
      items={emailSetupDropdownItems}
      isLocked={emailSetupSelectDisabled}
      onSelect={async (value) => handleEmailSetupSelectionChange(value)}
      noSelectedErrorMessage={showPlaceholder ? "Choose setup..." : undefined}
      noSelectedErrorMessageShowAsRed={false}
      dropdownWidthPx={420}
      fullwidth
    />
  );

  const privacyEmailEditor = (
    <div className="mb-[20px] max-w-[520px]">
      <label className="mb-[6px] block text-[13px] font-medium text-neutral-700">
        Privacy Email Address
      </label>
      <ToolTip text={postmarkEmailLockTooltip}>
        <span className="inline-block">
          <TextInputBase
            field="privacy-email"
            value={privacyEmailInput}
            onChange={(event) => {
              privacyEmailInitializedRef.current = true;
              setPrivacyEmailInput(event.target.value);
            }}
            placeholder="privacy@example.com"
            inputWidth="w-[320px]"
            inputTextSize="text-[14px] text-neutral-800"
            disabled={postmarkUpsertRequest?.running || postmarkEmailLocked}
          />
        </span>
      </ToolTip>
      {privacyEmailValidationError && (
        <div className="mt-[8px] text-[13px] text-red-600">{privacyEmailValidationError}</div>
      )}
      {postmarkEmailLocked && (
        <AlertCard
          variant="warning"
          size="compact"
          title="Postmark email is locked"
          description={`This email is used by child organizations: ${sharedChildOrganizationNames}. Remove sharing before changing it.`}
          containerClasses="mt-[12px] mb-0"
        />
      )}
      <div className="mt-[12px]">
        <ToolTip text={postmarkEmailLockTooltip}>
          <span className="inline-block">
            <Button
              color="primary"
              onClick={() => {
                void handlePrivacyEmailSave();
              }}
              disabled={
                postmarkEmailLocked ||
                !privacyEmailInput.trim() ||
                Boolean(privacyEmailValidationError) ||
                postmarkUpsertRequest?.running ||
                privacyEmailInput.trim().toLowerCase() === privacyEmailSaved
              }
            >
              {postmarkUpsertRequest?.running
                ? "Saving..."
                : privacyEmailSaved
                ? "Update email"
                : "Save email"}
            </Button>
          </span>
        </ToolTip>
      </div>
    </div>
  );

  return (
    <div
      className={clsx(
        "rounded-[24px] bg-white p-[36px]",
        !hideCardChrome &&
          "border border-solid border-neutral-300 shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
        className,
      )}
    >
      <DomainSetupStepSection
        number="1"
        title="Fill in your domain address"
        childrenClassName="mt-[8px]"
        onViewed={() => trackStepViewed("domain_address")}
      >
        <div className="w-full">
          <div>
            <div className="grid gap-[12px]">
              <div>
                <label className="block text-[13px] font-medium text-neutral-700 mb-[6px]">
                  Privacy Center Root Domain
                </label>
                <TextInputBase
                  field="primary-domain"
                  value={privacyRootDomainInput}
                  onChange={(event) => {
                    privacyDomainInitializedRef.current = true;
                    setPrivacyRootDomainInput(event.target.value);
                  }}
                  placeholder="acme.org"
                  inputWidth="w-[320px]"
                  inputTextSize="text-[14px] text-neutral-800"
                  disabled={privacyDomainUpdateRequest?.running}
                />
              </div>
              {privacySubdomainSuffix && (
                <div>
                  <label className="block text-[13px] font-medium text-neutral-700 mb-[6px]">
                    Privacy Sub Domain
                  </label>
                  <TextInputBase
                    field="privacy-subdomain"
                    value={privacySubdomainLabel}
                    onChange={(event) => {
                      privacyDomainInitializedRef.current = true;
                      setPrivacySubdomainLabel(sanitizeSubdomainLabel(event.target.value));
                    }}
                    onBlur={() => {
                      setPrivacySubdomainLabel(
                        (current) => sanitizeSubdomainLabel(current) || "privacy",
                      );
                    }}
                    placeholder="privacy"
                    inputWidth="w-[120px]"
                    inputTextSize="text-[14px] text-neutral-800"
                    disabled={privacyDomainUpdateRequest?.running}
                    rightAddonText={`.${privacySubdomainSuffix}`}
                  />
                </div>
              )}
            </div>
            {primaryDomainValidationError && (
              <div className="mt-[8px] text-[13px] text-red-600">
                {primaryDomainValidationError}
              </div>
            )}
            <div className="mt-[16px]">
              <Button
                color="primary"
                onClick={() => {
                  handlePrimaryDomainSaveRequest();
                }}
                disabled={
                  !privacyRootDomainInput.trim() ||
                  Boolean(primaryDomainValidationError) ||
                  privacyDomainUpdateRequest?.running ||
                  (privacyRootDomainInput.trim().toLowerCase() === privacyRootDomainSaved &&
                    privacySubdomainValue === privacySubdomainSavedValue)
                }
              >
                {privacyDomainUpdateRequest?.running
                  ? "Saving..."
                  : hasSavedDomain
                  ? "Update domain"
                  : "Save domain"}
              </Button>
            </div>
            {!hasSavedDomain && (
              <div className="mt-[12px] rounded-[8px] bg-[#F5F3FF] px-[12px] py-[8px] text-[13px] text-[#5A4ACB]">
                Save your domain to unlock the DNS and verification steps.
              </div>
            )}
          </div>
        </div>
      </DomainSetupStepSection>
      {showDomainChangeConfirmModal && (
        <Modal
          closeButton={true}
          showOverlay={true}
          closeOnOverlayClick={true}
          title="Change Domain Settings?"
          body="Changing your root domain or privacy subdomain updates only your Privacy Center domain. You'll need to verify its CNAME record again."
          firstButton="Cancel"
          secondButton="Yes, Update Domain"
          firstButtonColor="tertiary"
          secondButtonColor="primary"
          alignText="left"
          alignButton="right"
          onFirstButtonClick={() => {
            setShowDomainChangeConfirmModal(false);
            setPendingDomainChange(null);
          }}
          onClose={() => {
            setShowDomainChangeConfirmModal(false);
            setPendingDomainChange(null);
          }}
          onSecondButtonClick={handleConfirmPrimaryDomainChange}
        />
      )}
      {showShareConfirmModal && pendingShareOption && (
        <Modal
          closeButton={true}
          showOverlay={true}
          closeOnOverlayClick={true}
          title={`Use ${pendingShareOption.ownerOrganizationName}'s privacy email?`}
          body={
            <div className="space-y-4">
              <p>
                This means that {effectiveOrg?.name ?? "this organization"}'s listed privacy email
                will be updated to {pendingShareOption.emailAddress}.
              </p>
              <p>
                Privacy requests and email replies will appear in the{" "}
                {pendingShareOption.ownerOrganizationName} inbox and any emails sent will come from{" "}
                {pendingShareOption.emailAddress}.
              </p>
            </div>
          }
          firstButton="Cancel"
          secondButton={postmarkShareUpsertRequest?.running ? "Saving..." : "Use parent email"}
          firstButtonColor="tertiary"
          secondButtonColor="primary"
          alignText="left"
          alignButton="right"
          onFirstButtonClick={() => {
            setShowShareConfirmModal(false);
            setPendingShareOwnerId(null);
          }}
          onClose={() => {
            setShowShareConfirmModal(false);
            setPendingShareOwnerId(null);
          }}
          onSecondButtonClick={() => {
            void handleConfirmShare();
          }}
        />
      )}

      {hasSavedDomain && (
        <>
          {shouldShowEmailSetupStep && (
            <DomainSetupStepSection
              number="2"
              title="Select the privacy email to send DSAR replies from"
              onViewed={() => trackStepViewed("privacy_email_selection")}
            >
              {!isSharedPostmark && !postmarkMailbox && privacyEmailEditor}
              <div className="mb-[16px] max-w-[420px]">
                <div className="rounded-[6px] border border-solid border-neutral-300 bg-white px-[10px] py-[8px] text-[14px] font-normal">
                  {emailSetupDropdown(false)}
                </div>
              </div>
              {isSharedPostmark && sharedPostmarkMailbox && (
                <div className="space-y-[12px]">
                  <div className="rounded-[8px] bg-[#F7F7FB] px-[12px] py-[10px] text-[13px] leading-[19px] text-neutral-700">
                    Email DNS, forwarding, and inbox setup are managed by{" "}
                    {sharedPostmarkMailbox.ownerOrganizationName}. Incoming emails and consumer
                    replies to {sharedPostmarkMailbox.emailAddress} appear only in the parent org
                    inbox.
                  </div>
                </div>
              )}
            </DomainSetupStepSection>
          )}

          {emailSetupSelectionComplete && usesOwnedPostmarkMailbox && (
            <DomainSetupStepSection
              number={forwardingStepNumber}
              title="Set up email forwarding"
              onViewed={() => trackStepViewed("email_forwarding")}
              subtitle={
                <div className="space-y-[12px]">
                  <div>
                    Using Google Workspace?{" "}
                    <DomainSetupDocumentationLink href="https://developers.truevault.com/docs/email-setup/google-workspace-default-routing">
                      Follow these instructions
                    </DomainSetupDocumentationLink>
                  </div>
                  <div>
                    Using Microsoft 365?{" "}
                    <DomainSetupDocumentationLink href="https://developers.truevault.com/docs/email-setup/microsoft-365-groups">
                      Follow these instructions
                    </DomainSetupDocumentationLink>
                  </div>
                </div>
              }
            >
              {privacyEmailEditor}
              {postmarkMailbox ? (
                <ol className="custom custom-sublist domain-setup-step-list p-0 flex w-full flex-col gap-[12px] pl-[24px]">
                  <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                    <div className="flex-1">Sign in to your email provider’s admin console.</div>
                  </li>
                  <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                    <div className="flex-1">
                      Create or update the forwarding rule for your privacy inbox using the details
                      below.
                    </div>
                  </li>
                  <li className="flex w-full items-start">
                    <DomainSetupInlineCopyField label="Your Address:" value={emailForwardAddress} />
                  </li>
                  <li className="flex w-full items-start">
                    <DomainSetupInlineCopyField label="Forward to:" value={inboundAddress} />
                  </li>
                  <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                    <div className="flex-1">
                      Click <span className="font-bold">Save.</span>
                    </div>
                  </li>
                </ol>
              ) : (
                <div className="rounded-[8px] bg-[#F5F3FF] px-[12px] py-[8px] text-[13px] text-[#5A4ACB]">
                  Customized instructions will appear once the proposed email address has been
                  saved.
                </div>
              )}
            </DomainSetupStepSection>
          )}

          {emailSetupSelectionComplete && canShowSetupDetails && (
            <DomainSetupStepSection
              number={dnsStepNumber}
              onViewed={() => trackStepViewed("dns_records")}
              title={
                usesOwnedPostmarkMailbox
                  ? "Add DNS records to your domain host to set up your privacy email and your custom domain for your Privacy Center"
                  : "Add your privacy domain CNAME record to your DNS host"
              }
              subtitle={
                <span>
                  First time adding DNS records?{" "}
                  <DomainSetupDocumentationLink href="https://developers.truevault.com/docs/domain-setup/dns-record-configuration">
                    Here's a how-to guide.
                  </DomainSetupDocumentationLink>
                </span>
              }
            >
              <ol className="custom custom-sublist domain-setup-step-list p-0 flex w-full flex-col gap-[12px] pl-[24px]">
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="min-w-0 flex-1">
                    Login to your domain host (For example: Google Domains, GoDaddy, Hover and
                    similar).
                  </div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="min-w-0 flex-1">Click on your company’s domain.</div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="min-w-0 flex-1">
                    Find the menu which says <span className="font-bold">DNS</span> or{" "}
                    <span className="font-bold">DNS Records</span> and click on it.
                  </div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="min-w-0 flex-1">
                    Click <span className="font-bold">Add New Record</span> (It might say “Create
                    new record” or something similar).
                  </div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="min-w-0 flex-1">
                    Add the following records:
                    <div className="mt-[6px] overflow-x-hidden px-[8px]">
                      {dnsRecords.length ? (
                        <DomainSetupDnsRecordsTable
                          records={dnsRecords}
                          renderStatus={(row) => <RecordStatusIcon state={row.state} />}
                        />
                      ) : (
                        <div className="mt-[16px] rounded-[12px] border border-dashed border-[#B8B5C5] bg-neutral-0 px-[16px] py-[12px] text-[13px] text-neutral-600">
                          {usesOwnedPostmarkMailbox
                            ? "DNS records will appear here after you save your domain and Postmark processes the setup."
                            : "Save your domain to view the required CNAME record."}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
                <li className="flex w-full items-start text-[14px] leading-[21px] text-[#1A1726]">
                  <div className="flex-1">
                    Click <span className="font-bold">Save.</span>
                  </div>
                </li>
              </ol>
            </DomainSetupStepSection>
          )}

          {emailSetupSelectionComplete && canShowSetupDetails && (
            <DomainSetupStepSection
              number={verificationStepNumber}
              onViewed={() => trackStepViewed("setup_verification")}
              title={
                usesOwnedPostmarkMailbox
                  ? "Test and verify email and custom domain setup"
                  : "Test and verify custom domain setup"
              }
            >
              <div className="space-y-[16px]">
                <div className="space-y-[12px]">
                  {statusItems.map((item) => (
                    <StatusRow
                      key={item.label}
                      label={item.label}
                      state={item.state}
                      detail={item.detail}
                      disabled={item.disabled}
                    />
                  ))}
                </div>
                {usesOwnedPostmarkMailbox ? (
                  <div className="rounded-[8px] bg-primary-50 px-[12px] py-[10px] text-primary-700">
                    Testing email forwarding and DNS setup should take a few minutes. If it takes
                    longer, refresh this page to check the latest status.
                  </div>
                ) : (
                  <div>We'll verify your privacy domain CNAME setup.</div>
                )}
                <div>
                  {verificationError && (
                    <div className="mb-[8px] rounded-[8px] bg-red-50 px-[12px] py-[8px] text-[12px] text-red-700">
                      {verificationError}
                    </div>
                  )}
                  <Button
                    color="primary"
                    onClick={() => {
                      void handleVerification();
                    }}
                    disabled={verificationDisabled}
                    loading={verificationRunning}
                  >
                    {usesOwnedPostmarkMailbox
                      ? "Test email and DNS records"
                      : "Test custom domain DNS record"}
                  </Button>
                </div>
              </div>
            </DomainSetupStepSection>
          )}

          {canShowSetupDetails && allPostmarkSetupComplete && (
            <div className="ml-[40px] mt-[16px]">
              <AlertCard
                variant="success"
                title={`Email Forwarding and DNS Records are successfully set up for ${setupCompleteDomainLabel}.`}
                containerClasses="!mt-0 !mb-0"
              />
            </div>
          )}

          {emailSetupSelectionComplete && canShowSetupDetails && usesOwnedPostmarkMailbox && (
            <DomainSetupStepSection
              number={logoStepNumber}
              title="Add a company logo for email branding"
              onViewed={() => trackStepViewed("email_branding_logo")}
              subtitle={
                <span>
                  All emails that TrueVault sends to consumers on your behalf will include your
                  company logo at the top. Logo in .png or .jpg preferred. Maximum file size is 1MB;
                  max height of 100px.
                </span>
              }
            >
              <div className="grid gap-[16px]">
                {messageOrg.messageSettings?.headerImageUrl && (
                  <div className="max-w-[320px]">
                    <img
                      src={messageOrg.messageSettings.headerImageUrl}
                      className="max-h-[120px] border border-dashed border-neutral-300 rounded-[8px] p-[12px] bg-neutral-0"
                    />
                  </div>
                )}
                <div>
                  <Button
                    color={messageOrg.messageSettings?.headerImageUrl ? "tertiary" : "primary"}
                    onClick={() => setLogoDialogOpen(true)}
                  >
                    {messageOrg.messageSettings?.headerImageUrl ? "Change Logo" : "Upload Logo"}
                  </Button>
                </div>
                <LogoUploadDialog
                  acceptedTypes={ACCEPTED_FILE_TYPES}
                  logoType="MESSAGE_HEADER"
                  title="Upload Email Header Logo"
                  organizationId={messageOrg.id}
                  open={logoDialogOpen}
                  onClose={() => setLogoDialogOpen(false)}
                  onDone={async (logoUrl: string) => {
                    await emailSettings.updateMessageSettings({ logoUrl });
                    trackStepCompleted("email_branding_logo", "upload_logo");
                  }}
                  initialImage={messageOrg.messageSettings?.headerImageUrl}
                  omitHelpImage={true}
                  imageRecommendation={
                    <span>
                      Logo in .PNG or .JPG preferred. Maximum file size is 1MB. Maximum height of
                      100px.
                    </span>
                  }
                />
              </div>
            </DomainSetupStepSection>
          )}

          {emailSetupSelectionComplete && canShowSetupDetails && usesOwnedPostmarkMailbox && (
            <DomainSetupStepSection
              number={testEmailStepNumber}
              title="Send a test email to see how it looks"
              onViewed={() => trackStepViewed("test_email")}
              subtitle={
                <span>
                  A test email will be sent from your new privacy email to {testEmailRecipient}
                </span>
              }
            >
              <div className="flex flex-wrap items-center gap-[12px]">
                <ToolTip text={testEmailDisabledReason}>
                  <span
                    className="inline-block"
                    tabIndex={testEmailDisabledReason ? 0 : undefined}
                    aria-label={testEmailDisabledReason}
                  >
                    <Button
                      color={testEmailSent ? "tertiary" : "primary"}
                      size="s"
                      disabled={Boolean(testEmailDisabledReason)}
                      onClick={() => {
                        void handleSendTestEmail();
                      }}
                    >
                      {emailSettings.testMessageRunning
                        ? "Sending..."
                        : testEmailSent
                        ? "Resend Test Email"
                        : "Send test email"}
                    </Button>
                  </span>
                </ToolTip>
                {testEmailSent && (
                  <div className="w-full max-w-[720px]">
                    <AlertCard
                      variant="success"
                      title={`A test email was successfully sent from ${emailForwardAddress}`}
                      containerClasses="!mt-0 !mb-0"
                    />
                  </div>
                )}
              </div>
            </DomainSetupStepSection>
          )}
        </>
      )}
    </div>
  );
};
